import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { useAnalysisGenerationActions } from 'redux/actions/AnalysisGenerations'
import { useSnackbarActions } from 'redux/actions/Snackbar'
import {
  useCompletedAnalysisGenerations,
  useGenerationPolling
} from 'redux/hooks/AnalysisGenerations'
import { useAnalysisType } from 'redux/hooks/AnalysisTypes'
import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import { useTranslation } from 'language/translator'

import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import Link from 'components_new/atoms/Link'
import Snackbar from 'components_new/atoms/Snackbar'
import Text from 'components_new/atoms/Text'

import {
  AnalysisGeneration,
  AnalysisGenerationStatus
} from 'types/AnalysisGeneration'
import { SnackbarMessage } from 'types/GlobalSnackbar'

/**
 * Polls analysis runs on every page and tells the person who started a run
 * that it has finished, unless its run dialog is still open and shows the
 * outcome itself. The snackbar stays until it is closed or followed to the
 * analysis, so it never interrupts. Runs are shared per customer, the
 * snackbar is not.
 */
const AnalysisCompletedNotifier = () => {
  useGenerationPolling()

  const { account } = useAccountSettings()
  const completed = useCompletedAnalysisGenerations()
  const { dismissCompleted } = useAnalysisGenerationActions()
  const { toggleSnackbarOpen } = useSnackbarActions()
  const { pathname } = useLocation()
  const { t } = useTranslation()

  // Kept after closing so the snackbar keeps its text while it animates out.
  const [ready, setReady] = useState<AnalysisGeneration | null>(null)
  const [readyOpen, setReadyOpen] = useState(false)

  const { analysisType } = useAnalysisType(ready?.analysis_type_id ?? null)

  const next = account
    ? Object.values(completed).find((entry) => entry.created_by === account.id)
    : undefined

  // Taken out of the store once announced, so it is announced only once. A
  // rerun happens in place on the analysis: there its outcome is only a line.
  useEffect(() => {
    if (readyOpen || !next) return

    dismissCompleted(next.id)

    const analysisId = next.rerun_analysis_id

    if (analysisId && next.status !== AnalysisGenerationStatus.SUCCEEDED) {
      toggleSnackbarOpen(SnackbarMessage.AnalysisRerunFailed, {
        duration: 6000
      })

      return
    }

    if (analysisId && pathname === `/analyses/${analysisId}`) {
      toggleSnackbarOpen(SnackbarMessage.AnalysisRerun)

      return
    }

    setReady(next)
    setReadyOpen(true)
  }, [readyOpen, next, dismissCompleted, pathname, toggleSnackbarOpen])

  const resultId =
    ready?.status === AnalysisGenerationStatus.SUCCEEDED
      ? ready.analysis_id
      : null

  const statusTitle = (() => {
    switch (ready?.status) {
    case AnalysisGenerationStatus.SUCCEEDED:
      return t('analysis_types', 'done_title')
    case AnalysisGenerationStatus.ABORTED:
      return t('analysis_types', 'run_aborted_title')
    default:
      return t('analysis_types', 'run_failed_title')
    }
  })()

  return (
    <Snackbar
      action={
        <>
          {resultId ? (
            <Link
              color="brand.light"
              href={`/analyses/${resultId}`}
              onClick={() => setReadyOpen(false)}
            >
              {t('global', 'go_to')}
            </Link>
          ) : null}
          <IconButton
            color="inherit"
            onClick={() => setReadyOpen(false)}
            size="small"
            sx={{ ml: 1 }}
          >
            <Icon fontSize="small" name="Close" />
          </IconButton>
        </>
      }
      message={
        <>
          {analysisType ? (
            <Text color="inherit" fontWeight="bold" variant="body2">
              {analysisType.name}
            </Text>
          ) : null}
          {statusTitle}
        </>
      }
      open={readyOpen}
      sx={{ position: 'static' }}
    />
  )
}

export default AnalysisCompletedNotifier
